import { useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  LoaderCircle,
  RefreshCw,
  Scale,
} from "lucide-react";

const money = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  maximumFractionDigits: 2,
});

const percent = new Intl.NumberFormat("es-AR", {
  maximumFractionDigits: 4,
});

const PAYMENT_STATUS = [
  { value: "pending", label: "Pendiente" },
  { value: "partially_paid", label: "Pago parcial" },
  { value: "paid", label: "Pagado" },
  { value: "exempt", label: "Exento" },
  { value: "review", label: "En revisión" },
];

const REGIME_OVERRIDES = [
  { value: "", label: "Inferir del expediente" },
  { value: "ordinary_percentage", label: "General · 2%" },
  { value: "peace_or_tax", label: "Paz o Tributaria" },
  { value: "labor", label: "Laboral · 2% según resultado" },
  { value: "labor_agreement", label: "Acuerdo OCL / SRT / OCC" },
  { value: "possible_exemption", label: "Exención confirmada" },
];

function numericInput(value) {
  return value === null || value === undefined ? "" : value;
}

function displayedBalance(value) {
  const total = Number(value?.totalAssessed || 0);
  const credit = Number(value?.previousContributionCredit || 0);
  const paid = (value?.obligations || []).reduce(
    (sum, obligation) => sum + Number(obligation.paidAmount || 0),
    0,
  );
  return Math.max(0, total - credit - paid);
}

function evidenceLabel(evidence) {
  return `Documento ${evidence.documentIndex}, pág. ${evidence.page}`;
}

function ReadOnlyValue({ label, children }) {
  return (
    <div className="workflow-contribution-readonly-value">
      <span>{label}</span>
      <strong>{children || "—"}</strong>
    </div>
  );
}

function ObligationCard({ obligation, readOnly, onChange }) {
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const patch = (changes) => onChange({ ...obligation, ...changes });

  return (
    <article className="workflow-contribution-obligation">
      <header>
        <div>
          <small>{obligation.instanceLabel}</small>
          <strong>{obligation.concept}</strong>
        </div>
        <div className="workflow-contribution-obligation-total">
          <strong>{money.format(Number(obligation.assessedAmount || 0))}</strong>
          {obligation.reviewRequired && !obligation.reviewed ? (
            <span className="is-review"><AlertTriangle size={13} /> Revisar</span>
          ) : (
            <span className="is-ready"><CheckCircle2 size={13} /> Verificado</span>
          )}
        </div>
      </header>

      <div className="workflow-contribution-formula">
        <span>
          Base {money.format(Number(obligation.basis || 0))}
          {obligation.manualBasis !== null && " · ajustada manualmente"}
        </span>
        <span>
          {obligation.rate > 0
            ? `${percent.format(obligation.rate)}%`
            : "Importe fijo o exento"}
          {obligation.appealFactor
            ? ` · ${obligation.appealFactor}% del aporte inicial`
            : ""}
          {obligation.minimumApplied ? " · mínimo aplicado" : ""}
        </span>
      </div>

      {readOnly ? (
        <div className="workflow-contribution-readonly-grid">
          <ReadOnlyValue label="Base legal">
            {money.format(Number(obligation.basis || 0))}
          </ReadOnlyValue>
          <ReadOnlyValue label="Fecha de base">{obligation.basisDate}</ReadOnlyValue>
          <ReadOnlyValue label="Aportante inicial">
            {obligation.initialContributor}
          </ReadOnlyValue>
          <ReadOnlyValue label="Condenado / responsable final">
            {obligation.finalLiableParty}
          </ReadOnlyValue>
          <ReadOnlyValue label="Estado">
            {PAYMENT_STATUS.find(({ value }) => (
              value === obligation.paymentStatus
            ))?.label}
          </ReadOnlyValue>
          <ReadOnlyValue label="Pagado">
            {money.format(Number(obligation.paidAmount || 0))}
          </ReadOnlyValue>
        </div>
      ) : (
        <div className="workflow-contribution-edit-grid">
          <label>
            Base manual <span>Opcional</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={numericInput(obligation.manualBasis)}
              placeholder={String(obligation.suggestedBasis ?? "")}
              onChange={(event) => patch({
                manualBasis: event.target.value === ""
                  ? null
                  : Number(event.target.value),
              })}
            />
          </label>
          <label>
            Fecha de base
            <input
              type="date"
              value={obligation.basisDate || ""}
              onChange={(event) => patch({ basisDate: event.target.value })}
            />
          </label>
          <label>
            Aportante inicial
            <input
              type="text"
              maxLength={500}
              value={obligation.initialContributor || ""}
              onChange={(event) => patch({
                initialContributor: event.target.value,
              })}
            />
          </label>
          <label>
            Condenado / responsable final
            <input
              type="text"
              maxLength={500}
              value={obligation.finalLiableParty || ""}
              onChange={(event) => patch({
                finalLiableParty: event.target.value,
              })}
            />
          </label>
          <label>
            Proporción <span>Opcional</span>
            <div className="workflow-contribution-suffix-input">
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={numericInput(obligation.sharePercent)}
                onChange={(event) => patch({
                  sharePercent: event.target.value === ""
                    ? null
                    : Number(event.target.value),
                })}
              />
              <span>%</span>
            </div>
          </label>
          <label>
            Vencimiento <span>Opcional</span>
            <input
              type="date"
              value={obligation.dueDate || ""}
              onChange={(event) => patch({ dueDate: event.target.value })}
            />
          </label>
          <label>
            Estado del pago
            <select
              value={obligation.paymentStatus || "pending"}
              onChange={(event) => patch({ paymentStatus: event.target.value })}
            >
              {PAYMENT_STATUS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>
          <label>
            Importe pagado
            <input
              type="number"
              min="0"
              step="0.01"
              value={numericInput(obligation.paidAmount)}
              onChange={(event) => patch({
                paidAmount: event.target.value === ""
                  ? 0
                  : Number(event.target.value),
              })}
            />
          </label>
          <label>
            Fecha de pago <span>Opcional</span>
            <input
              type="date"
              value={obligation.paidDate || ""}
              onChange={(event) => patch({ paidDate: event.target.value })}
            />
          </label>
          <label className="workflow-contribution-checkbox">
            <input
              type="checkbox"
              checked={Boolean(obligation.jointAndSeveral)}
              onChange={(event) => patch({
                jointAndSeveral: event.target.checked,
              })}
            />
            Obligación solidaria
          </label>
          <label className="workflow-contribution-checkbox">
            <input
              type="checkbox"
              checked={Boolean(obligation.reviewed)}
              onChange={(event) => patch({ reviewed: event.target.checked })}
            />
            Revisado por una persona
          </label>
          <label className="workflow-contribution-notes">
            Notas <span>Opcional</span>
            <textarea
              rows={2}
              maxLength={5_000}
              value={obligation.notes || ""}
              onChange={(event) => patch({ notes: event.target.value })}
            />
          </label>
        </div>
      )}

      {obligation.costOrderSummary && (
        <p className="workflow-contribution-cost-order">
          <strong>Costas:</strong> {obligation.costOrderSummary}
        </p>
      )}
      {(obligation.evidence || []).length > 0 && (
        <div className="workflow-contribution-evidence">
          <button
            type="button"
            onClick={() => setEvidenceOpen((current) => !current)}
          >
            {evidenceOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {evidenceOpen ? "Ocultar evidencia" : "Ver evidencia de la sentencia"}
          </button>
          {evidenceOpen && (
            <div>
              {obligation.evidence.map((evidence, index) => (
                <blockquote key={`${evidence.documentIndex}:${evidence.page}:${index}`}>
                  <small>{evidenceLabel(evidence)}</small>
                  <p>“{evidence.quote}”</p>
                </blockquote>
              ))}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export function WorkflowLegalContributionField({
  field,
  sourceValue,
  value,
  onChange,
  onResolve,
  resolving = false,
  readOnly = false,
}) {
  const calculated = sourceValue?.status === "calculated";
  const current = value?.sourceCalculationId === sourceValue?.calculationId
    && !value?.classificationStale;
  const updateObligation = (updated) => onChange({
    ...value,
    obligations: (value.obligations || []).map((obligation) => (
      obligation.id === updated.id ? updated : obligation
    )),
  });

  return (
    <section className="workflow-record-field workflow-contribution-field">
      <span className="workflow-record-label workflow-record-automated-label">
        <Scale size={13} />
        {field.label}{field.required && <strong> *</strong>}
      </span>

      {!calculated ? (
        <div className="workflow-contribution-empty">
          <strong>Pendiente del cálculo de intereses</strong>
          <span>Primero analizá las sentencias y terminá la liquidación.</span>
        </div>
      ) : !value || !current ? (
        <div className="workflow-contribution-empty">
          <strong>Aforo pendiente</strong>
          <span>
            Se consultará el expediente y se aplicará la versión vigente de la
            Ley 5059.
          </span>
          {!readOnly && (
            <button type="button" disabled={resolving} onClick={onResolve}>
              {resolving
                ? <LoaderCircle className="spin" size={15} />
                : <Scale size={15} />}
              {resolving ? "Calculando…" : "Calcular aporte"}
            </button>
          )}
        </div>
      ) : (
        <div className="workflow-contribution-panel">
          <header className="workflow-contribution-summary">
            <div>
              <small>Total determinado</small>
              <strong>{money.format(Number(value.totalAssessed || 0))}</strong>
            </div>
            <div>
              <small>Saldo</small>
              <strong>{money.format(displayedBalance(value))}</strong>
            </div>
            <span className={value.requiresReview ? "is-review" : "is-ready"}>
              {value.requiresReview
                ? <><AlertTriangle size={14} /> Requiere revisión</>
                : <><CheckCircle2 size={14} /> Listo</>}
            </span>
          </header>

          <div className="workflow-contribution-regime">
            <div>
              <small>Régimen aplicado</small>
              <strong>{value.regime?.label}</strong>
              <span>
                {value.legalBasis} · reglas {value.rulesVersion}
              </span>
            </div>
            <div>
              <small>Clasificación del expediente</small>
              <strong>{[
                value.caseClassification?.fuero,
                value.caseClassification?.materia,
              ].filter(Boolean).join(" · ") || "Sin clasificar"}</strong>
            </div>
          </div>

          {!readOnly && (
            <div className="workflow-contribution-controls">
              <label>
                Fecha del aforo
                <input
                  type="date"
                  value={value.assessmentDate || ""}
                  onChange={(event) => onChange({
                    ...value,
                    assessmentDate: event.target.value,
                  })}
                />
              </label>
              <label>
                Régimen
                <select
                  value={value.regimeOverride || ""}
                  onChange={(event) => onChange({
                    ...value,
                    regimeOverride: event.target.value,
                  })}
                >
                  {REGIME_OVERRIDES.map(({ value: option, label }) => (
                    <option key={option} value={option}>{label}</option>
                  ))}
                </select>
              </label>
              <label>
                Aportes anteriores / crédito
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={numericInput(value.previousContributionCredit)}
                  onChange={(event) => onChange({
                    ...value,
                    previousContributionCredit: event.target.value === ""
                      ? 0
                      : Number(event.target.value),
                  })}
                />
              </label>
              <button type="button" disabled={resolving} onClick={onResolve}>
                {resolving
                  ? <LoaderCircle className="spin" size={15} />
                  : <RefreshCw size={15} />}
                {resolving ? "Recalculando…" : "Recalcular"}
              </button>
            </div>
          )}

          {(value.warnings || []).length > 0 && (
            <div className="workflow-contribution-warnings">
              {value.warnings.map((warning) => (
                <p key={warning.code}>
                  <AlertTriangle size={14} />
                  <span>{warning.message}</span>
                </p>
              ))}
            </div>
          )}

          <div className="workflow-contribution-obligations">
            {(value.obligations || []).map((obligation) => (
              <ObligationCard
                obligation={obligation}
                readOnly={readOnly}
                onChange={updateObligation}
                key={obligation.id}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

